/**
 * HTTP Pairing Gateway
 *
 * Production implementation of pairing gateway using REST API.
 * Makes HTTP requests to ANYW3AR backend for immediate pairing.
 *
 * Story 4.9: Integrates HTTP interceptor for automatic 401 handling
 * - Token refresh on 401 responses
 * - Session expiration callback
 * - Single retry attempt
 */

import type {
  IPairingGateway,
  PairingCode,
  PairingResult,
  PairingErrorCode,
  OwnerNfcScanContextResponse,
} from '@/domain/ports/IPairingGateway';
import { PairingError } from '@/domain/ports/IPairingGateway';
import { createAuthenticatedFetch, type AuthenticatedFetch } from './httpInterceptor';

/**
 * HTTP Pairing Gateway Implementation
 *
 * Communicates with ANYW3AR backend API for NFC product pairing operations.
 * Uses authenticated fetch wrapper for automatic token handling and 401 retry.
 */
export class HttpPairingGateway implements IPairingGateway {
  private baseUrl: string;
  private authenticatedFetch: AuthenticatedFetch;

  constructor(
    baseUrl: string,
    getAuthToken: () => string | null,
    refreshToken: () => Promise<string | null>,
    onSessionExpired: () => void
  ) {
    this.baseUrl = baseUrl;
    this.authenticatedFetch = createAuthenticatedFetch({
      getToken: getAuthToken,
      refreshToken,
      onSessionExpired,
    });
  }

  /**
   * Get scan context for NFC URL (determines routing based on item status)
   *
   * Story 4.9: Uses authenticatedFetch for automatic 401 handling
   */
  async getScanContext(nfcUrl: string): Promise<OwnerNfcScanContextResponse> {
    try {
      const encodedUrl = encodeURIComponent(nfcUrl);
      const response = await this.authenticatedFetch(
        `${this.baseUrl}/api/v1/owner/nfc/context?url=${encodedUrl}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw this.mapApiError(errorData, response.status);
      }

      const data = await response.json();
      return data as OwnerNfcScanContextResponse;
    } catch (error) {
      if (error instanceof PairingError) {
        throw error;
      }

      // Network or other errors
      throw new PairingError(
        error instanceof Error ? error.message : 'Network error',
        'NetworkError'
      );
    }
  }

  /**
   * Pair NFC product immediately with 2-word code
   *
   * Story 4.9: Uses authenticatedFetch for automatic 401 handling
   */
  async pairImmediately(nfcUrl: string, code: PairingCode): Promise<PairingResult> {
    try {
      const response = await this.authenticatedFetch(
        `${this.baseUrl}/api/v1/owner/pairing`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nfcUrl,
            code: {
              first: code.first.toLowerCase(),
              second: code.second.toLowerCase(),
            },
            timestamp: new Date().toISOString(),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw this.mapApiError(errorData, response.status);
      }

      const data = await response.json();

      return {
        linkedNfcId: data.linkedNfcId,
        productInfo: {
          productId: data.productInfo.productId,
          title: data.productInfo.title,
          imageUrl: data.productInfo.imageUrl,
          editionSize: data.productInfo.editionSize,
        },
        pairedAt: data.pairedAt,
        itemNumber: data.itemNumber,
      };
    } catch (error) {
      if (error instanceof PairingError) {
        throw error;
      }

      // Network or other errors
      throw new PairingError(
        error instanceof Error ? error.message : 'Network error',
        'NetworkError'
      );
    }
  }

  /**
   * Unpair item from Trophy Room
   *
   * Calls the future endpoint POST /api/v1/owner/trophy-room/items/:itemId/unpair
   * This endpoint is planned for Story 3.6.b (backend) and will be fully integrated
   * in Story 3.6.d (frontend).
   *
   * Story 4.9: Uses authenticatedFetch for automatic 401 handling
   */
  async unpairItem(itemId: string): Promise<void> {
    try {
      const response = await this.authenticatedFetch(
        `${this.baseUrl}/api/v1/owner/trophy-room/items/${itemId}/unpair`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw this.mapApiError(errorData, response.status);
      }

      // Unpair successful (204 No Content or 200 OK expected)
    } catch (error) {
      if (error instanceof PairingError) {
        throw error;
      }

      // Network or other errors
      throw new PairingError(
        error instanceof Error ? error.message : 'Network error',
        'NetworkError'
      );
    }
  }

  /**
   * Map API error response to typed PairingError
   *
   * Priority:
   * 1. Backend error field (e.g., "PairingBlockedError")
   * 2. Specific error codes (e.g., "InvalidSignature")
   * 3. HTTP status codes (401, 403, 404)
   */
  private mapApiError(errorData: any, status: number): PairingError {
    const message = errorData.error || errorData.message || 'Unknown error';
    const backendError = errorData.error; // Backend sends "Product cannot be paired", "InvalidCode", etc.
    const code = errorData.code as PairingErrorCode;

    // Priority 1: Check status field for stolen/lost items (Story 3.6.b.2)
    // Backend response: { error: "Product cannot be paired", status: "stolen", message: "..." }
    const itemStatus = errorData.status;
    if (itemStatus === 'stolen' || itemStatus === 'lost') {
      return new PairingError(
        errorData.message || 'This product has been reported as stolen or lost',
        'ProductStolen'
      );
    }

    // Priority 2: Map specific error codes from backend
    switch (code || backendError) {
      case 'InvalidSignature':
        return new PairingError('Invalid NFC signature', 'InvalidSignature');

      case 'ProductStolen':
        return new PairingError('This product has been reported as stolen', 'ProductStolen');

      case 'CodeUsed':
        return new PairingError('This code has already been used', 'CodeUsed');

      case 'InvalidCode':
        return new PairingError('Invalid code format', 'InvalidCode');

      default:
        // Priority 3: Map HTTP status codes ONLY for auth errors
        if (status === 401) {
          return new PairingError('Authentication required', 'AuthenticationRequired');
        }

        if (status === 403) {
          // 403 WITHOUT business error → likely permission issue
          return new PairingError('Access forbidden', 'UnknownError');
        }

        if (status === 404) {
          // Check if this is a "Code not found" error (invalid pairing code)
          const fullMessage = (errorData.message || message).toLowerCase();
          if (fullMessage.includes('code not found')) {
            return new PairingError('Invalid code', 'InvalidCode');
          }
          return new PairingError('Resource not found', 'UnknownError');
        }

        return new PairingError(message, 'UnknownError');
    }
  }
}
